import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { useWorkspace } from "@/context/WorkspaceContext";
import { useWorkspaceRole } from "@/hooks/useWorkspaceRole";
import { getWorkspaceInvitations, getWorkspaceMembers } from "@/services/workspaceService";
import { WorkspaceInvitation, WorkspaceMember, WorkspaceRole } from "@/types/workspace";

/** Members + pending invitations for the active workspace, plus the derived seat
 *  numbers. Extracted so the General panel's seat meter and the Members panel's
 *  table compute seats from one implementation rather than two. (Each call site
 *  still owns its own state and fetch — only one settings panel is mounted at a
 *  time, so they never both run.)
 *
 *  Seat maths counts members AND pending invitations, which is what the backend
 *  `_check_member_limit` does — the old UI counted members only, so the Invite
 *  button could be enabled while the API rejected with `member_limit_exceeded`. */
export function useWorkspaceMembers() {
    const { activeWorkspace, billingAccount } = useWorkspace();
    const { isWorkspaceOwner } = useWorkspaceRole();

    const [members, setMembers] = useState<WorkspaceMember[]>([]);
    const [membersLoading, setMembersLoading] = useState(false);
    const [pendingInvitations, setPendingInvitations] = useState<WorkspaceInvitation[]>([]);
    const [invitesLoading, setInvitesLoading] = useState(false);

    const workspaceId = activeWorkspace?.id ?? null;

    // Keep separate guards per endpoint. Owners trigger both members and
    // invitations loads; a shared counter can make one response discard the
    // other even when both are for the current workspace.
    const membersRequestRef = useRef(0);
    const invitationsRequestRef = useRef(0);

    const loadMembers = useCallback(async (): Promise<void> => {
        if (workspaceId == null) {
            setMembers([]);
            return;
        }
        const seq = ++membersRequestRef.current;
        try {
            setMembersLoading(true);
            const response = await getWorkspaceMembers(workspaceId);
            if (seq !== membersRequestRef.current) return;
            setMembers(response);
        } catch {
            if (seq !== membersRequestRef.current) return;
            toast.error("Failed to load workspace members");
            setMembers([]);
        } finally {
            if (seq === membersRequestRef.current) setMembersLoading(false);
        }
    }, [workspaceId]);

    // Invitations are owner-only server-side, so members legitimately see none.
    const loadInvitations = useCallback(async (): Promise<void> => {
        if (workspaceId == null || !isWorkspaceOwner) {
            setPendingInvitations([]);
            return;
        }
        const seq = ++invitationsRequestRef.current;
        try {
            setInvitesLoading(true);
            const response = await getWorkspaceInvitations(workspaceId);
            if (seq !== invitationsRequestRef.current) return;
            setPendingInvitations(
                response
                    .filter((invitation) => invitation.status === "pending")
                    .sort(
                        (a, b) =>
                            new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
                    ),
            );
        } catch {
            if (seq !== invitationsRequestRef.current) return;
            toast.error("Failed to load pending invitations");
            setPendingInvitations([]);
        } finally {
            if (seq === invitationsRequestRef.current) setInvitesLoading(false);
        }
    }, [workspaceId, isWorkspaceOwner]);

    useEffect(() => {
        void loadMembers();
        void loadInvitations();
    }, [loadMembers, loadInvitations]);

    /** Owner first, then by join date — the owner row is the anchor of the table. */
    const sortedMembers = useMemo(
        () =>
            [...members].sort((left, right) => {
                if (left.role === WorkspaceRole.Owner && right.role !== WorkspaceRole.Owner) return -1;
                if (right.role === WorkspaceRole.Owner && left.role !== WorkspaceRole.Owner) return 1;
                return new Date(left.joined_at).getTime() - new Date(right.joined_at).getTime();
            }),
        [members],
    );

    const memberLimit = billingAccount?.member_limit ?? 10;
    const seatsUsed = members.length + pendingInvitations.length;
    const isMemberLimitReached = seatsUsed >= memberLimit;
    /** Meter fill + tone, shared so the General and Members readouts can't drift.
     *  Neutral until 80% of the cap, amber approaching it, red at it. */
    const seatPct = memberLimit > 0 ? Math.min(100, Math.round((seatsUsed / memberLimit) * 100)) : 0;
    const seatBarClass = isMemberLimitReached
        ? "vb-seat-bar vb-seat-bar--at"
        : seatPct >= 80
          ? "vb-seat-bar vb-seat-bar--near"
          : "vb-seat-bar";

    // Seats are only trustworthy for owners: `loadInvitations` returns [] for
    // everyone else, so a member's count would silently omit pending invites.
    const canReadSeats = isWorkspaceOwner && billingAccount != null;

    return {
        canReadSeats,
        members,
        sortedMembers,
        membersLoading,
        pendingInvitations,
        invitesLoading,
        loading: membersLoading || invitesLoading,
        loadMembers,
        loadInvitations,
        memberLimit,
        seatsUsed,
        isMemberLimitReached,
        seatPct,
        seatBarClass,
    };
}

export default useWorkspaceMembers;
